import { COMMANDS } from '../../utils/commonProperties';
import * as THREE from 'three';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCommandMessage, resetOperation } from '../../redux/operationSlice';
import { clearSelection } from '../../redux/selectionSlice';
import { useComponentWiring } from '../../modules/wiring';
import { generateUniqueId } from '../../utils/generateUniqueId';
import parseColor from '../../utils/parseColor';
import { createBellButton } from '../../factory/symbols/createBellSymbols';

// 🆕 Undo/Redo imports
import { BellButtonCommand } from '../../modules/undoRedo';

const useBellButton = (scene, camera, renderer) => {
    const dispatch = useDispatch();
    const { commandType } = useSelector((state) => state.operation);
    const settings = useSelector(state => state.settings);
    const wiringActiveRef = useRef(false);
    const sceneRef = useRef(null);
    const [bellWireSnapPoints, setBellWireSnapPoints] = useState([]);

    // derive wire color from settings.lowCurrent
    const parsedWire = parseColor(settings?.lowCurrent?.wireColor ?? 0x0045ff);
    const wireColor = parsedWire.colorNumber;

    // 🆕 BellButton Configuration
    const bellButtonConfig = {
        commandType: COMMANDS.DRAW_BELL_BUTTON,
        itemTypeName: 'bellButton',
        wireColor: parsedWire.colorNumber,
        allowSpaceKey: false, // ✅ BellButton'da Space ile ekleme yok, sadece Enter
        skipItemExecution: true, // ✅ addItem()'da execute atlanır, handleWireEnd'de yapılır
        createSymbol: (position, direction, angle, isPreview, itemType, extraData = {}) => {
            const colorSetting = isPreview
                ? (settings?.lowCurrent?.wirePreviewColor ?? parsedWire.colorNumber)
                : parsedWire.colorNumber;
            const parsed = parseColor(colorSetting);

            return createBellButton(
                null,
                position,
                parsed.colorNumber,
                isPreview,
                direction
            );
        },
        // ❌ createCommand KALDIRILDI - BellButton için command sadece handleWireEnd içinde oluşturulur
        createCommand: null,
        // ❌ createFeederData null - BellButton için feeder oluşturma yok (handleWireEnd'de yapılır)
        createFeederData: null,
        // ❌ createComponentData null - BellButton için component oluşturma yok (handleWireEnd'de yapılır)
        createComponentData: null,
        itemTypes: [
            { name: 'Zil Butonu', type: 'bellButton', power: 0 }
        ],
        onKeyDown: null
    };

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_BELL_BUTTON || !scene || !camera || !renderer) return;
        dispatch(setCommandMessage('Zil butonu: Önce bir zil hattı üzerinde nokta seçin.'));
        sceneRef.current = scene;

        // ✅ Bell wire snap noktalarını topla
        const snapPoints = [];
        scene.traverse((obj) => {
            if (obj.userData?.function === 'wireBell' && obj.geometry) {
                const positions = obj.geometry.attributes.position;
                for (let i = 0; i < positions.count; i++) {
                    const point = new THREE.Vector3(
                        positions.getX(i),
                        positions.getY(i),
                        positions.getZ(i)
                    );
                    snapPoints.push({
                        position: point,
                        type: 'bellWireVertex',
                        wireObject: obj
                    });
                }
            }
        });

        setBellWireSnapPoints(snapPoints);
        wiringActiveRef.current = true;

        // ✅ Cleanup: commandType değiştiğinde wiring'i devre dışı bırak
        return () => {
            wiringActiveRef.current = false;
        };
    }, [commandType, scene, camera, renderer, dispatch]);

    // ✅ BellButton placement ve removal handlers
    const handleBellButtonPlace = (position, direction) => {
         return null;
    };

    const handleBellButtonRemove = (bellButton) => {
        if (!bellButton) return;
        sceneRef.current.remove(bellButton);
        bellButton.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
        });
    };

    // Wire end handler
    const handleWireEnd = (vertices, bellButtons, source, bellButton, tempLineUUID, wireMarkers) => {
        // BellButton data (bellButtons array veya direkt bellButton parametresi)
        const bellButtonData = bellButton || (bellButtons && bellButtons.length > 0 ? bellButtons[0] : null);
        if (!bellButtonData) {
            console.warn('No bellButton data found');
            return;
        }
        // ✅ ID'ler
        const branchID = generateUniqueId('branchBellButton');
        const wireID = generateUniqueId('wireBellButton');
        const buttonId = generateUniqueId('bellButton');

        // ✅ Bell button açısını hesapla (önizleme ile aynı - wire yönüne göre)
        const lastVertex = vertices[vertices.length - 1];
        const prevVertex = vertices[vertices.length - 2] || vertices[vertices.length - 1];
        const direction = new THREE.Vector3().subVectors(lastVertex, prevVertex);
        const buttonAngle = Math.atan2(direction.y, direction.x);

        // 🆕 BellButtonCommand oluştur, pass settings-derived color
        const bellButtonCommand = new BellButtonCommand(
            'ADD',
            {
                buttonId: buttonId,
                buttonPosition: [lastVertex.x, lastVertex.y, lastVertex.z],
                buttonAngle: buttonAngle,
                wireId: wireID,
                branchID: branchID,
                vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                color: wireColor
            }
        );

        bellButtonCommand.execute(scene, dispatch);

        dispatch(setCommandMessage('Zil butonu yerleştirildi.'));
        dispatch(resetOperation());
        dispatch(clearSelection()); // ✅ Selection'ı temizle - ESC'ye gerek kalmaz
        wiringActiveRef.current = false;

        return { wireId: wireID, branchId: branchID };
    };

    // Item add handler
    const handleItemAdd = (item) => {
    };

    // Use the component wiring hook
    useComponentWiring(
        scene,
        camera,
        renderer,
        bellWireSnapPoints, // ✅ Bell wire snap points - sadece bell wire'lara snap olur
        {
            ...bellButtonConfig,
            enabled: () => wiringActiveRef.current,
            onWireEnd: handleWireEnd,
            onSocketPlace: handleBellButtonPlace,
            onItemAdd: handleItemAdd,
            onSocketRemove: handleBellButtonRemove,
            onPreviewUpdate: () => {},
            onWireCancel: () => {
                wiringActiveRef.current = false;
                dispatch(resetOperation());
                dispatch(setCommandMessage('Zil butonu iptal edildi.'));
            }
        }
    );
};

export default useBellButton;